import { CircleAlert, Lock, RefreshCw, SearchX, WifiOff } from "lucide-react"
import type * as React from "react"
import { useTranslation } from "react-i18next"
import { isApiError } from "@/api/client"
import { describeError } from "@/api/mutation"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Spinner } from "@/components/ui/spinner"
import { cn } from "@/lib/utils"

export interface EmptyStateProps {
  icon?: React.ComponentType<{ className?: string }>
  title: React.ReactNode
  description?: React.ReactNode
  /** Primary call to action (usually a Button). */
  action?: React.ReactNode
  secondaryAction?: React.ReactNode
  /** `card`: dashed bordered box (inside a page); `plain`: no border (inside tables/panels). */
  variant?: "card" | "plain"
  size?: "sm" | "default" | "lg"
  className?: string
  children?: React.ReactNode
}

/** Friendly placeholder for "nothing here yet" with an optional primary action. */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  variant = "card",
  size = "default",
  className,
  children,
}: EmptyStateProps) {
  return (
    <div
      data-slot="empty-state"
      className={cn(
        "flex w-full flex-col items-center justify-center text-center text-balance",
        variant === "card" && "rounded-xl border border-dashed",
        size === "sm" && "gap-2 px-4 py-8",
        size === "default" && "gap-3 px-6 py-14",
        size === "lg" && "gap-4 px-6 py-20",
        className,
      )}
    >
      {Icon && (
        <div
          className={cn(
            "bg-muted text-muted-foreground mb-1 flex items-center justify-center rounded-xl ring-1 ring-foreground/5",
            size === "lg" ? "size-12" : "size-10",
          )}
        >
          <Icon className={size === "lg" ? "size-5.5" : "size-5"} />
        </div>
      )}
      <div className="max-w-md space-y-1">
        <h3 className={cn("font-medium", size === "lg" ? "text-base" : "text-sm")}>{title}</h3>
        {description && <p className="text-muted-foreground text-sm text-pretty">{description}</p>}
      </div>
      {children}
      {(action || secondaryAction) && (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  )
}

export interface ErrorStateProps {
  error?: unknown
  title?: React.ReactNode
  description?: React.ReactNode
  onRetry?: () => void
  retrying?: boolean
  variant?: "card" | "plain"
  className?: string
}

/** Error placeholder that understands {@link ApiError} (offline, forbidden, not found…). */
export function ErrorState({
  error,
  title,
  description,
  onRetry,
  retrying,
  variant = "card",
  className,
}: ErrorStateProps) {
  const { t } = useTranslation()
  let icon = CircleAlert
  let defaultTitle = t("errors.loadFailed")
  if (isApiError(error)) {
    if (error.isNetwork) {
      icon = WifiOff
      defaultTitle = t("errors.offlineTitle")
    } else if (error.isForbidden) {
      icon = Lock
      defaultTitle = t("errors.forbiddenTitle")
    } else if (error.isNotFound) {
      icon = SearchX
      defaultTitle = t("errors.notFoundTitle")
    }
  }
  return (
    <EmptyState
      icon={icon}
      variant={variant}
      className={cn(variant === "card" && "border-destructive/25", className)}
      title={title ?? defaultTitle}
      description={description ?? (error ? describeError(error, t) : undefined)}
      action={
        onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry} disabled={retrying}>
            {retrying ? <Spinner /> : <RefreshCw />}
            {t("common.retry")}
          </Button>
        )
      }
    />
  )
}

/** Centered spinner with an optional label. */
export function LoadingState({ label, className }: { label?: string; className?: string }) {
  const { t } = useTranslation()
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn("text-muted-foreground flex items-center justify-center gap-2 py-16 text-sm", className)}
    >
      <Spinner />
      <span>{label ?? t("common.loading")}</span>
    </div>
  )
}

/** Full-viewport loading (app boot, auth checks). */
export function FullPageLoader() {
  return (
    <div className="bg-background flex min-h-dvh items-center justify-center">
      <Spinner className="text-muted-foreground size-5" />
    </div>
  )
}

export function TableSkeleton({
  rows = 6,
  columns = 4,
  className,
}: {
  rows?: number
  columns?: number
  className?: string
}) {
  return (
    <div className={cn("overflow-hidden rounded-xl border", className)} aria-hidden>
      <div className="bg-muted/40 flex gap-4 border-b px-4 py-3">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-3 flex-1" style={{ maxWidth: i === 0 ? "10rem" : "6rem" }} />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 border-b px-4 py-3 last:border-0">
          {Array.from({ length: columns }).map((_, c) => (
            <Skeleton
              key={c}
              className="h-4 flex-1"
              style={{ maxWidth: c === 0 ? "14rem" : `${5 + ((r + c) % 3)}rem` }}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

export function FormSkeleton({ fields = 4, className }: { fields?: number; className?: string }) {
  return (
    <div className={cn("space-y-5", className)} aria-hidden>
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-3.5 w-28" />
          <Skeleton className="h-8 w-full" />
        </div>
      ))}
    </div>
  )
}

export function CardSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("space-y-3 rounded-xl border p-5", className)} aria-hidden>
      <Skeleton className="h-4 w-1/3" />
      <Skeleton className="h-3 w-2/3" />
      <Skeleton className="h-3 w-1/2" />
    </div>
  )
}

/** Skeleton for a whole page: header + content blocks. */
export function PageSkeleton() {
  return (
    <div className="mx-auto w-full max-w-5xl space-y-8 px-4 pt-8 sm:px-6 lg:px-8" aria-hidden>
      <div className="space-y-2">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-80 max-w-full" />
      </div>
      <TableSkeleton />
    </div>
  )
}
